import React, { FunctionComponent, memo } from 'react';
import TextField from '@material-ui/core/TextField';
import { InputComponentProps } from './InputComponent.types';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';

const customColorInput = (main: string) =>
  createMuiTheme({
    overrides: {
      MuiFormLabel: {
        root: {
          color: main,
          '&.Mui-focused': {
            color: main,
          },
        },
      },
      MuiOutlinedInput: {
        notchedOutline: {
          borderColor: `${main} !important`,
        },
        root: {
          borderColor: main,
          '&:hover': {
            borderColor: `${main} !important`,
          },
        },
      },
      MuiInputBase: {
        root: {
          color: main,
        },
      },
    },
  });

export const InputComponent: FunctionComponent<InputComponentProps> = memo(
  props => {
    const {
      className,
      color = 'primary',
      customColor,
      defaultValue,
      disabled = false,
      error = false,
      fullWidth = false,
      helperText,
      id,
      inputRef,
      inputAdornment,
      label,
      margin = 'normal',
      textAreaMode = false,
      name,
      onChange = () => {},
      onKeyPress,
      placeholder,
      required = false,
      rows,
      rowsMax,
      size = 'medium',
      type = 'text',
      value,
      variant = 'standard',
    } = props;
    let inputProps;
    if (inputAdornment) {
      const { position, adornment, onClick } = inputAdornment;
      inputProps = {
        [`${position}Adornment`]: (
          <InputAdornment position={position}>
            {typeof adornment === 'string' ? (
              adornment
            ) : (
              <div onClick={onClick}>
                <IconButton>{adornment}</IconButton>
              </div>
            )}
          </InputAdornment>
        ),
      };
    }
    const restProps = Object.keys(props)
      .filter(
        key =>
          key !== 'inputAdornment' &&
          key !== 'inputSize' &&
          key !== 'textAreaMode' &&
          key !== 'customColor' &&
          key !== 'inputRef'
      )
      .reduce((res, key) => Object.assign(res, { [key]: props[key] }), {});
    if (customColor) {
      const customTheme = customColorInput(customColor);
      return (
        <ThemeProvider theme={customTheme}>
          <TextField
            {...restProps}
            className={className}
            color={color}
            defaultValue={defaultValue}
            disabled={disabled}
            error={error}
            fullWidth={fullWidth}
            helperText={helperText}
            id={id}
            inputRef={inputRef}
            label={label}
            margin={margin}
            multiline={textAreaMode}
            name={name}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            rows={rows}
            rowsMax={rowsMax}
            size={size}
            type={type}
            value={value}
            variant={variant}
            InputProps={inputProps}
            onKeyPress={onKeyPress}
          />
        </ThemeProvider>
      );
    } else {
      return (
        <TextField
          {...restProps}
          className={className}
          color={color}
          defaultValue={defaultValue}
          disabled={disabled}
          error={error}
          fullWidth={fullWidth}
          helperText={helperText}
          id={id}
          inputRef={inputRef}
          label={label}
          margin={margin}
          multiline={textAreaMode}
          name={name}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          rows={rows}
          rowsMax={rowsMax}
          size={size}
          type={type}
          value={value}
          variant={variant}
          InputProps={inputProps}
          onKeyPress={onKeyPress}
        />
      );
    }
  }
);

InputComponent.displayName = 'InputComponent';
InputComponent.whyDidYouRender = true;
